/**
 * DrawerHeader.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Header compacto do drawer que mostra o contexto do slot selecionado.
 * Exibe título, data, horário de início e badge de disponibilidade.
 *
 * @module DrawerHeader
 */

import React from 'react';
import { WEEKDAYS_LONG, MONTHS } from '../../constants/calendarConstants';
import styles from './AppointmentDrawer.module.css';

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const XIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/**
 * Formata a data do slot para exibição.
 * Exemplo: "22 Maio 2026 • Sexta"
 *
 * @param {Date} date
 * @returns {string}
 */
function formatSlotDate(date) {
  if (!date) return '';
  const day = date.getDate();
  const month = MONTHS[date.getMonth()];
  const year = date.getFullYear();
  const weekday = WEEKDAYS_LONG[date.getDay()];
  return `${day} ${month} ${year} • ${weekday}`;
}

/**
 * Formata apenas o horário de início do slot.
 * Exemplo: "08:00"
 *
 * @param {number} hour
 * @param {number} minute
 * @returns {string}
 */
function formatSlotTime(hour, minute) {
  const startH = String(hour).padStart(2, '0');
  const startM = String(minute).padStart(2, '0');
  return `${startH}:${startM}`;
}

/**
 * Calcula o horário de fim dado início e duração em minutos.
 * Exemplo: hora=9, minute=0, duration=50 → "09:50"
 *
 * @param {number} hour
 * @param {number} minute
 * @param {number} durationMinutes
 * @returns {string}
 */
function formatEndTime(hour, minute, durationMinutes) {
  const totalMinutes = hour * 60 + minute + durationMinutes;
  const endH = Math.floor(totalMinutes / 60) % 24;
  const endM = totalMinutes % 60;
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
}

/**
 * Header compacto do drawer com contexto do slot selecionado.
 *
 * @param {Object} props
 * @param {Object} props.slot - { date: Date, hour: number, minute: number }
 * @param {string} props.title - Título do drawer
 * @param {boolean} [props.isAvailable=true] - Se o slot está disponível
 * @param {boolean} [props.isOffProfessionalHours=false] - Livre, mas fora do horário próprio do profissional
 * @param {Function} props.onClose - Fechar drawer
 * @param {number} [props.duration=0] - Duração em minutos (0 = não definida)
 * @returns {React.ReactElement}
 */
export default function DrawerHeader({
  slot,
  title,
  isAvailable = true,
  isOffProfessionalHours = false,
  onClose,
  duration = 0,
}) {
  const dateLabel = slot?.date ? formatSlotDate(slot.date) : '';
  const startTime = slot ? formatSlotTime(slot.hour, slot.minute) : '';
  const endTime = (slot && duration > 0)
    ? formatEndTime(slot.hour, slot.minute, duration)
    : null;

  return (
    <div className={styles.drawerHeader}>
      {/* ── Linha do título ─────────────────────────────────────────────────── */}
      <div className={styles.drawerHeaderTop}>
        <h2 className={styles.drawerTitle}>{title}</h2>
        <button
          className={styles.drawerCloseBtn}
          onClick={onClose}
          aria-label="Fechar"
          title="Fechar"
          type="button"
        >
          <XIcon size={20} />
        </button>
      </div>

      {/* ── Separador ───────────────────────────────────────────────────────── */}
      <div className={styles.drawerHeaderDivider} />

      {/* ── Contexto do slot ────────────────────────────────────────────────── */}
      {slot && (
        <div className={styles.slotContext}>
          <div className={styles.slotContextLeft}>
            <span className={styles.slotContextIcon} aria-hidden="true">
              <CalendarIcon />
            </span>
            <div className={styles.slotContextInfo}>
              <span className={styles.slotDate}>{dateLabel}</span>
              <div className={styles.slotTimeRow}>
                <span className={styles.slotTime}>{startTime}</span>
                {endTime && (
                  <>
                    <span className={styles.slotTimeArrow}>→</span>
                    <span className={styles.slotTimeEnd}>{endTime}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          {!isAvailable ? (
            <span className={`${styles.availabilityBadge} ${styles.badgeOccupied}`}>Ocupado</span>
          ) : isOffProfessionalHours ? (
            <span className={`${styles.availabilityBadge} ${styles.badgeWarning}`}>Fora do horário</span>
          ) : (
            <span className={`${styles.availabilityBadge} ${styles.badgeAvailable}`}><CheckIcon /> Disponível</span>
          )}
        </div>
      )}
    </div>
  );
}
